import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AccessTimeOutlined, ContentCopy, LinkOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { invitesService, permissionsService, rbacService } from "../../services";

const EXPIRATION_OPTIONS = [
  { value: 6, label: "6 h" },
  { value: 12, label: "12 h" },
  { value: 24, label: "24 h" },
];

const EXCLUDED_PERMISSION_CODES = new Set([
  "manage_companies",
  "manage_roles",
  "manage_permissions",
  "assign_extra_permissions",
]);

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeCompany = (source) => {
  const item = source?.company || source?.branch || source;
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id,
    name: item.name || item.alias || item.legalName || `Sucursal ${item.id}`,
  };
};

const normalizePermission = (source) => {
  if (!source || typeof source !== "object") return null;
  return {
    ...source,
    friendly_name:
      source.friendly_name ||
      source.label_es ||
      source.name ||
      source.code ||
      `Permiso ${source.id}`,
    caption: source.caption || source.description || "",
  };
};

const permissionFriendlyLabel = (permission) =>
  permission?.friendly_name || permission?.label_es || permission?.name || permission?.code || "Permiso";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const buildInviteLink = (payload) => {
  const directLink = String(payload?.invite_link || "").trim();
  if (directLink) return directLink;

  const rawToken = String(payload?.invite_token || "").trim();
  if (!rawToken || typeof window === "undefined") return "";
  const query = new URLSearchParams({ token: rawToken }).toString();
  return `${window.location.origin}/invite/accept?${query}`;
};

function SummaryItem({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.action.hover,
        borderRadius: 1,
        px: 1.5,
        py: 1.25,
      })}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

function InviteCreatePage() {
  const { isSuperuser } = useAuth();
  const copyTimerRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [expiresInHours, setExpiresInHours] = useState(12);
  const [selectedPermissionCodes, setSelectedPermissionCodes] = useState([]);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const clearGeneratedInvite = useCallback(() => {
    setGeneratedInvite(null);
    setCopied(false);
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoadingCompanies(true);
      try {
        const [companiesData, permissionsData] = await Promise.all([
          rbacService.listCompanies(),
          permissionsService.list(),
        ]);
        if (!active) return;
        setCompanies(toArray(companiesData).map(normalizeCompany).filter(Boolean));
        setPermissions(toArray(permissionsData).map(normalizePermission).filter(Boolean));
      } catch {
        if (!active) return;
        setCompanies([]);
        setPermissions([]);
        showSnack("No se pudo cargar la configuracion inicial.", "error");
      } finally {
        if (active) setLoadingCompanies(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [showSnack]);

  useEffect(() => {
    let active = true;

    if (!selectedBranchId) {
      setRoles([]);
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoadingRoles(true);
      try {
        const rolesData = await rbacService.getRolesByCompany(selectedBranchId);
        if (!active) return;
        setRoles(
          toArray(rolesData).filter((role) => role && role.is_active !== false)
        );
      } catch {
        if (!active) return;
        setRoles([]);
        showSnack("No se pudieron cargar los roles de la sucursal.", "error");
      } finally {
        if (active) setLoadingRoles(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [selectedBranchId, showSnack]);

  const visiblePermissions = useMemo(() => {
    return toArray(permissions)
      .filter((permission) => permission && !EXCLUDED_PERMISSION_CODES.has(permission.code))
      .sort((a, b) => {
        const left = String(permissionFriendlyLabel(a) || "").toLowerCase();
        const right = String(permissionFriendlyLabel(b) || "").toLowerCase();
        return left.localeCompare(right);
      })
      .slice(0, 20);
  }, [permissions]);

  const selectedBranch = useMemo(
    () => companies.find((company) => String(company.id) === String(selectedBranchId)) || null,
    [companies, selectedBranchId]
  );

  const selectedRole = useMemo(
    () => roles.find((role) => String(role.id) === String(selectedRoleId)) || null,
    [roles, selectedRoleId]
  );

  const expirationLabel = useMemo(
    () =>
      EXPIRATION_OPTIONS.find((option) => option.value === Number(expiresInHours))?.label || `${expiresInHours} h`,
    [expiresInHours]
  );
  const generationLocked = Boolean(generatedInvite?.invite_link);

  const togglePermission = useCallback((code) => {
    clearGeneratedInvite();
    setSelectedPermissionCodes((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    );
  }, [clearGeneratedInvite]);

  const handleGenerate = useCallback(async () => {
    if (!selectedBranchId) return;
    if (generationLocked) {
      showSnack(
        "Ya generaste un link para esta configuracion. Cambia los datos si necesitas uno nuevo.",
        "warning"
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = await invitesService.create({
        expires_in_hours: Number(expiresInHours),
        invite_branches: [
          {
            branch_id: Number(selectedBranchId),
            role_id: selectedRoleId ? Number(selectedRoleId) : null,
            extra_permission_codes: selectedPermissionCodes,
            current: true,
          },
        ],
      });

      const inviteLink = buildInviteLink(payload);
      setGeneratedInvite({
        invite_link: inviteLink,
        expires_at: payload?.expires_at || null,
      });
      showSnack("Invitacion generada.", "success");
    } catch {
      setGeneratedInvite(null);
      showSnack("No se pudo generar la invitacion.", "error");
    } finally {
      setSubmitting(false);
    }
  }, [
    expiresInHours,
    generationLocked,
    selectedBranchId,
    selectedPermissionCodes,
    selectedRoleId,
    showSnack,
  ]);

  const handleCopy = useCallback(async () => {
    const link = String(generatedInvite?.invite_link || "").trim();
    if (!link) {
      showSnack("No hay link para copiar.", "warning");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      showSnack("No se pudo copiar el link.", "error");
    }
  }, [generatedInvite?.invite_link, showSnack]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ "& .MuiTypography-h4, & .MuiTypography-h5": { fontWeight: 500 } }}>
        <MainText
          icon={LinkOutlined}
          title="Invitaciones"
          subtitle="Links de acceso de un solo uso"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "380px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: 2,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: "0 12px 12px 0",
          })}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Configuracion de acceso
            </Typography>

            <FormControl fullWidth size="small" disabled={loadingCompanies}>
              <InputLabel>Sucursal *</InputLabel>
              <Select
                label="Sucursal *"
                value={selectedBranchId}
                onChange={(event) => {
                  clearGeneratedInvite();
                  setRoles([]);
                  setSelectedBranchId(event.target.value);
                  setSelectedRoleId("");
                }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={String(company.id)}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedBranchId || loadingRoles}>
              <InputLabel>Rol</InputLabel>
              <Select
                label="Rol"
                value={selectedRoleId}
                onChange={(event) => {
                  clearGeneratedInvite();
                  setSelectedRoleId(event.target.value);
                }}
              >
                <MenuItem value="">Sin rol</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={String(role.id)}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Tiempo de expiracion
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={Number(expiresInHours)}
                  onChange={(_, nextValue) => {
                    if (!nextValue) return;
                    clearGeneratedInvite();
                    setExpiresInHours(nextValue);
                  }}
                >
                  {EXPIRATION_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 500 }}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Permisos extra (opcional)
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.9 }}>
                {visiblePermissions.map((permission) => {
                  const selected = selectedPermissionCodes.includes(permission.code);
                  return (
                    <Box
                      key={permission.code}
                      component="button"
                      type="button"
                      onClick={() => togglePermission(permission.code)}
                      sx={{
                        p: 0,
                        m: 0,
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <Chip
                        size="small"
                        variant={selected ? "filled" : "outlined"}
                        color={selected ? "primary" : "default"}
                        label={
                          isSuperuser && permission?.code
                            ? `${permissionFriendlyLabel(permission)} (${permission.code})`
                            : permissionFriendlyLabel(permission)
                        }
                      />
                    </Box>
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {isSuperuser
                  ? "Superuser ve nombre, codigo tecnico y descripcion en roles/permisos."
                  : "Se muestran nombres amigables de permisos."}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Resumen
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                }}
              >
                <SummaryItem label="Sucursal" value={selectedBranch?.name || "Sin seleccionar"} />
                <SummaryItem label="Rol" value={selectedRole?.name || "Sin rol"} />
                <SummaryItem label="Expira en" value={expirationLabel} />
                <SummaryItem label="Permisos extra" value={String(selectedPermissionCodes.length)} />
              </Box>

              <Alert severity="info" sx={{ fontSize: 12 }}>
                El link es de un solo uso. Queda inactivo en cuanto el usuario crea su cuenta.
              </Alert>

              <Button
                variant="contained"
                fullWidth
                disabled={!selectedBranchId || submitting || generationLocked}
                onClick={handleGenerate}
              >
                {submitting ? "Generando..." : generationLocked ? "Link generado" : "Generar link"}
              </Button>

              {generationLocked ? (
                <Typography variant="caption" color="text.secondary">
                  Este link ya fue generado. Para crear otro, cambia la configuracion actual.
                </Typography>
              ) : null}
            </Stack>
          </Paper>

          {generatedInvite ? (
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 2,
                borderLeft: `3px solid ${theme.palette.success.main}`,
                borderRadius: "0 12px 12px 0",
              })}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1.25 }}>
                Link generado
              </Typography>

              <Alert severity="warning" sx={{ fontSize: 12, mb: 1.5 }}>
                Copia este link y compartelo directamente. No se enviara por ningun otro medio.
              </Alert>

              <Box
                sx={(theme) => ({
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  bgcolor: theme.palette.grey[100],
                  borderRadius: 1,
                  p: 1,
                })}
              >
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    fontFamily: "monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                  }}
                >
                  {generatedInvite.invite_link}
                </Typography>
                <Button size="small" startIcon={<ContentCopy />} onClick={handleCopy}>
                  {copied ? "Copiado ✓" : "Copiar"}
                </Button>
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25 }}>
                <AccessTimeOutlined sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Expira el {formatDateTime(generatedInvite.expires_at)} · Uso unico
                </Typography>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Box>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default InviteCreatePage;
